import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import adminService from '../../services/adminService';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

// Always fetch fresh headers from adminService (reads Bearer token from localStorage)
const getHeaders = () => ({ ...adminService.getAuthHeaders() });

const nm = {
  bg:          '#e8ecf1',
  shadow:      '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm:    '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const TASK_TYPES_KEI  = ['text-to-image','image-to-image','text-to-video','image-to-video','music','extend','music-video'];
const COST_TYPES      = ['fixed','per_second','per_image','per_video'];

const TASK_ACCENT = {
  'text-to-image':  '#7c3aed', 'image-to-image': '#6d28d9',
  'text-to-video':  '#5b21b6', 'image-to-video': '#4c1d95',
  'music':          '#8b5cf6', 'extend':         '#a78bfa',
  'music-video':    '#6d28d9',
};

const CATEGORY_ICON = {
  'text-to-image':  'Image', 'image-to-image': 'Image',
  'text-to-video':  'Video', 'image-to-video': 'Video',
  'music':          'Music', 'extend':         'Music',
  'music-video':    'Music',
};

const fmtDate = (str) => {
  if (!str) return '-';
  const d = new Date(str);
  return isNaN(d) ? String(str) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

// ── Small neumorphic button ────────────────────────────────────────────────────
const NmBtn = ({ onClick, children, accent = false, danger = false, disabled = false, small = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: disabled ? '#d1d5db' : accent ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : danger ? '#fef2f2' : nm.bg,
      color: disabled ? '#9ca3af' : accent ? '#fff' : danger ? '#dc2626' : '#475569',
      border: 'none', borderRadius: 10,
      padding: small ? '5px 11px' : '8px 16px',
      fontSize: small ? 11 : 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
      boxShadow: disabled ? 'none' : accent ? '0 4px 12px rgba(124,58,237,0.28)' : nm.shadowSm,
      transition: 'all 0.15s', whiteSpace: 'nowrap',
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.85'; }}
    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
    {children}
  </button>
);

// ── Pill badge ─────────────────────────────────────────────────────────────────
const TaskBadge = ({ type }) => (
  <span style={{
    background: `${TASK_ACCENT[type] || '#94a3b8'}18`,
    color: TASK_ACCENT[type] || '#64748b',
    padding: '2px 9px', borderRadius: 6, fontWeight: 700,
    fontSize: 9, letterSpacing: 0.6, textTransform: 'uppercase', whiteSpace: 'nowrap',
  }}>{type}</span>
);

const ProviderPill = ({ provider, accent }) => (
  <span style={{
    background: `${accent}18`, color: accent,
    padding: '2px 8px', borderRadius: 5, fontWeight: 700, fontSize: 9, letterSpacing: 0.5,
  }}>{provider}</span>
);

const PROVIDER_COLORS = {
  Anthropic: '#d97706', OpenAI: '#10b981', Google: '#3b82f6', Meta: '#6366f1',
  xAI: '#0f172a', Mistral: '#f59e0b', Cohere: '#8b5cf6', DeepSeek: '#06b6d4',
  Qwen: '#ec4899', Microsoft: '#0ea5e9', Perplexity: '#7c3aed', AI21: '#64748b',
};

// Section title bar
const SectionHead = ({ icon, title, sub, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(124,58,237,0.28)', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', letterSpacing: -0.2 }}>{title}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{sub}</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{right}</div>
  </div>
);

// Search bar
const SearchBar = ({ value, onChange, placeholder }) => (
  <div style={{ background: nm.bg, borderRadius: 10, padding: '7px 14px', boxShadow: nm.shadowInset, display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 180 }}>
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ background: 'transparent', border: 'none', outline: 'none', flex: 1, fontSize: 12, color: '#0f172a' }} />
    {value && <button onClick={() => onChange('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0 }}>×</button>}
  </div>
);

// Filter pill row
const FilterPills = ({ options, active, onChange }) => (
  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)} style={{
        padding: '4px 11px', borderRadius: 7, border: 'none', cursor: 'pointer',
        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
        background: active === o ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : nm.bg,
        color:      active === o ? '#fff'                                     : '#64748b',
        boxShadow:  active === o ? '0 3px 10px rgba(124,58,237,0.25)'        : nm.shadowSm,
        transition: 'all 0.15s', textTransform: 'uppercase',
      }}>{o}</button>
    ))}
  </div>
);

// Inline text / number input
const InlineInput = ({ value, onChange, type = 'text', width = '100%' }) => (
  <input type={type} value={value} onChange={e => onChange(e.target.value)}
    style={{ width, background: nm.bg, border: 'none', borderRadius: 7, padding: '5px 8px', fontSize: 11, color: '#0f172a', fontWeight: 600, boxShadow: nm.shadowInset, outline: 'none', boxSizing: 'border-box' }} />
);

const InlineSelect = ({ value, onChange, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
    style={{ background: nm.bg, border: 'none', borderRadius: 7, padding: '5px 8px', fontSize: 11, color: '#0f172a', fontWeight: 600, boxShadow: nm.shadowInset, outline: 'none' }}>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

// Toast
const Toast = ({ toast }) => (
  <AnimatePresence>
    {toast && (
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        style={{ position: 'fixed', bottom: 24, right: 24, padding: '13px 20px', borderRadius: 12, fontWeight: 700, fontSize: 13, zIndex: 9999,
          background: toast.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color:      toast.type === 'success' ? '#065f46' : '#991b1b',
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
        {toast.msg}
      </motion.div>
    )}
  </AnimatePresence>
);

// ──────────────────────────────────────────────────────────────────────────────
// PANEL A - Kei.ai Pricing (editable)
// ──────────────────────────────────────────────────────────────────────────────
function KeiPricingPanel({ onToast }) {
  const [rows,     setRows]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [saving,   setSaving]  = useState(null);
  const [deleting, setDeleting]= useState(null);
  const [edits,    setEdits]   = useState({});
  const [showAdd,  setShowAdd] = useState(false);
  const [newRow,   setNewRow]  = useState(emptyKeiRow());
  const [search,   setSearch]  = useState('');
  const [taskFilt, setTaskFilt]= useState('all');

  function emptyKeiRow() {
    return { model_slug: '', task_type: 'text-to-image', cost_type: 'fixed', cost_dgcc: '', min_cost_dgcc: '', description: '' };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/api/admin/model-pricing`, { headers: getHeaders() });
      const data = await res.json();
      setRows(Array.isArray(data.pricing) ? data.pricing : []);
    } catch { onToast('Failed to load Kei.ai pricing', 'error'); }
    finally  { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = r => setEdits(p => ({ ...p, [r.id]: { cost_dgcc: r.cost_dgcc, min_cost_dgcc: r.min_cost_dgcc ?? '', cost_type: r.cost_type || 'fixed', description: r.description || '' } }));
  const cancelEdit = id => setEdits(p => { const n = { ...p }; delete n[id]; return n; });
  const setEF = (id, f, v) => setEdits(p => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const save = async (row) => {
    const patch = edits[row.id]; if (!patch) return;
    setSaving(row.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/model-pricing/${row.id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ cost_dgcc: parseFloat(patch.cost_dgcc), min_cost_dgcc: patch.min_cost_dgcc !== '' ? parseFloat(patch.min_cost_dgcc) : null, cost_type: patch.cost_type, description: patch.description }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Update failed');
      onToast('Pricing updated', 'success'); cancelEdit(row.id); await load();
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(null); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.model_slug} / ${row.task_type}"?`)) return;
    setDeleting(row.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/model-pricing/${row.id}`, { method: 'DELETE', headers: getHeaders() });
      if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
      onToast('Row deleted', 'success'); await load();
    } catch (e) { onToast(e.message, 'error'); }
    finally { setDeleting(null); }
  };

  const add = async () => {
    if (!newRow.model_slug || !newRow.cost_dgcc) { onToast('Model slug and DGCC cost are required', 'error'); return; }
    setSaving('new');
    try {
      const res = await fetch(`${API_URL}/api/admin/model-pricing`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ ...newRow, cost_dgcc: parseFloat(newRow.cost_dgcc), min_cost_dgcc: newRow.min_cost_dgcc !== '' ? parseFloat(newRow.min_cost_dgcc) : null }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Create failed');
      onToast('Row added', 'success'); setNewRow(emptyKeiRow()); setShowAdd(false); await load();
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(null); }
  };

  const taskTypes = ['all', ...Array.from(new Set(rows.map(r => r.task_type)))];
  const visible = rows.filter(r => {
    const q = search.toLowerCase();
    return (!q || r.model_slug.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q))
        && (taskFilt === 'all' || r.task_type === taskFilt);
  });

  return (
    <div style={{ background: nm.bg, borderRadius: 18, padding: 24, boxShadow: nm.shadowSm }}>
      <SectionHead
        icon={<svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>}
        title="Kei.ai Model Pricing"
        sub="Manually set DGCC cost per generation for Image, Video and Music models"
        right={<>
          <NmBtn onClick={load}>Refresh</NmBtn>
          <NmBtn accent onClick={() => setShowAdd(v => !v)}>{showAdd ? 'Cancel' : '+ Add Row'}</NmBtn>
        </>}
      />

      {/* Add row panel */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{ background: nm.bg, borderRadius: 14, padding: 18, marginBottom: 16, boxShadow: nm.shadowInset, border: '1px solid rgba(124,58,237,0.12)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 }}>New Pricing Row</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10, marginBottom: 12 }}>
              {[
                { label:'Model Slug *', comp: <InlineInput value={newRow.model_slug} onChange={v => setNewRow(p => ({...p, model_slug: v}))} /> },
                { label:'Task Type', comp: <InlineSelect value={newRow.task_type} onChange={v => setNewRow(p => ({...p, task_type: v}))} options={TASK_TYPES_KEI} /> },
                { label:'Cost Type', comp: <InlineSelect value={newRow.cost_type} onChange={v => setNewRow(p => ({...p, cost_type: v}))} options={COST_TYPES} /> },
                { label:'DGCC Cost *', comp: <InlineInput value={newRow.cost_dgcc} onChange={v => setNewRow(p => ({...p, cost_dgcc: v}))} type="number" /> },
                { label:'Min DGCC', comp: <InlineInput value={newRow.min_cost_dgcc} onChange={v => setNewRow(p => ({...p, min_cost_dgcc: v}))} type="number" /> },
                { label:'Description', comp: <InlineInput value={newRow.description} onChange={v => setNewRow(p => ({...p, description: v}))} /> },
              ].map(({ label, comp }) => (
                <div key={label}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>{label}</div>
                  {comp}
                </div>
              ))}
            </div>
            <NmBtn accent onClick={add}>{saving === 'new' ? 'Saving…' : 'Save Row'}</NmBtn>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search model slug or description…" />
        <FilterPills options={taskTypes} active={taskFilt} onChange={setTaskFilt} />
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>Loading Kei.ai pricing…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
          {rows.length === 0 ? 'No pricing rows yet. Run migration 006 in Cloudflare D1 console, then click Refresh.' : 'No rows match your filter.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
            <thead>
              <tr style={{ background: '#f5f3ff' }}>
                {['Cat.','Model Slug','Task Type','Cost Type','DGCC / Gen','Min DGCC','Description','Updated','Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 9, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(124,58,237,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(row => {
                const editing = !!edits[row.id];
                const e = edits[row.id] || {};
                return (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', background: editing ? 'rgba(124,58,237,0.03)' : 'transparent' }}
                    onMouseEnter={ev => { if (!editing) ev.currentTarget.style.background = '#f5f3ff'; }}
                    onMouseLeave={ev => { if (!editing) ev.currentTarget.style.background = editing ? 'rgba(124,58,237,0.03)' : 'transparent'; }}>
                    {/* Category icon text */}
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontSize: 9, fontWeight: 800, color: TASK_ACCENT[row.task_type] || '#94a3b8' }}>{CATEGORY_ICON[row.task_type] || '-'}</span>
                    </td>
                    {/* Model slug - never editable */}
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', fontSize: 11 }}>{row.model_slug}</span>
                    </td>
                    {/* Task type */}
                    <td style={{ padding: '9px 10px' }}><TaskBadge type={row.task_type} /></td>
                    {/* Cost type */}
                    <td style={{ padding: '9px 10px' }}>
                      {editing
                        ? <InlineSelect value={e.cost_type} onChange={v => setEF(row.id, 'cost_type', v)} options={COST_TYPES} />
                        : <span style={{ color: '#64748b', fontSize: 11 }}>{row.cost_type}</span>}
                    </td>
                    {/* DGCC cost */}
                    <td style={{ padding: '9px 10px' }}>
                      {editing
                        ? <InlineInput value={e.cost_dgcc} onChange={v => setEF(row.id, 'cost_dgcc', v)} type="number" width={70} />
                        : <span style={{ fontWeight: 800, color: '#7c3aed', fontSize: 13 }}>{row.cost_dgcc}<span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 500, marginLeft: 3 }}>DGCC</span></span>}
                    </td>
                    {/* Min DGCC */}
                    <td style={{ padding: '9px 10px' }}>
                      {editing
                        ? <InlineInput value={e.min_cost_dgcc} onChange={v => setEF(row.id, 'min_cost_dgcc', v)} type="number" width={60} />
                        : <span style={{ color: '#64748b', fontSize: 11 }}>{row.min_cost_dgcc ?? '-'}</span>}
                    </td>
                    {/* Description */}
                    <td style={{ padding: '9px 10px', maxWidth: 200 }}>
                      {editing
                        ? <InlineInput value={e.description} onChange={v => setEF(row.id, 'description', v)} />
                        : <span style={{ color: '#64748b', fontSize: 11 }}>{row.description || '-'}</span>}
                    </td>
                    {/* Updated */}
                    <td style={{ padding: '9px 10px', color: '#94a3b8', fontSize: 10, whiteSpace: 'nowrap' }}>{fmtDate(row.updated_at)}</td>
                    {/* Actions */}
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {editing ? (
                          <>
                            <NmBtn accent small onClick={() => save(row)} disabled={saving === row.id}>{saving === row.id ? '…' : 'Save'}</NmBtn>
                            <NmBtn small onClick={() => cancelEdit(row.id)}>Cancel</NmBtn>
                          </>
                        ) : (
                          <>
                            <NmBtn small onClick={() => startEdit(row)}>Edit</NmBtn>
                            <NmBtn small danger onClick={() => del(row)} disabled={deleting === row.id}>{deleting === row.id ? '…' : 'Del'}</NmBtn>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10, color: '#94a3b8' }}>{rows.length} rows · Only image, video and music task types are managed here</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PANEL B - OpenRouter Pricing (read-only live view)
// ──────────────────────────────────────────────────────────────────────────────
function OpenRouterPricingPanel({ onToast }) {
  const [models,     setModels]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchedAt,  setFetchedAt]  = useState(null);
  const [search,     setSearch]     = useState('');
  const [provFilter, setProvFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/api/admin/openrouter-pricing`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setModels(Array.isArray(data.models) ? data.models : []);
      setFetchedAt(data.fetched_at || null);
    } catch (e) { onToast('OpenRouter fetch failed: ' + e.message, 'error'); }
    finally     { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const providers = ['all', ...Array.from(new Set(models.map(m => m.provider))).sort()];
  const visible = models.filter(m => {
    const q = search.toLowerCase();
    return (!q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q))
        && (provFilter === 'all' || m.provider === provFilter);
  });

  const freeLabel = (val) => val === 0 ? <span style={{ color: '#10b981', fontWeight: 700, fontSize: 10 }}>Free</span> : `$${val.toFixed(4)}`;

  return (
    <div style={{ background: nm.bg, borderRadius: 18, padding: 24, boxShadow: nm.shadowSm }}>
      <SectionHead
        icon={<svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>}
        title="OpenRouter Live Pricing"
        sub={fetchedAt ? `Live from openrouter.ai · Last synced ${new Date(fetchedAt).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}` : 'Live from openrouter.ai · Auto-synced · Read-only'}
        right={<>
          <div style={{ fontSize: 10, color: '#94a3b8', background: nm.bg, borderRadius: 8, padding: '4px 10px', boxShadow: nm.shadowInset, fontWeight: 600 }}>
            Chat &amp; Website Builder · Billed automatically from usage.cost
          </div>
          <NmBtn onClick={load} disabled={loading}>{loading ? 'Syncing…' : 'Sync Now'}</NmBtn>
        </>}
      />

      {/* Info callout */}
      <div style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.12)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span style={{ fontSize: 11, color: '#5b21b6', fontWeight: 600 }}>
          OpenRouter pricing is applied automatically at runtime from <code style={{ fontFamily: 'monospace', background: 'rgba(124,58,237,0.08)', padding: '1px 5px', borderRadius: 4 }}>usage.cost</code> returned per API call. No manual configuration needed.
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search model ID or name…" />
        <FilterPills options={providers} active={provFilter} onChange={setProvFilter} />
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>Fetching live pricing from OpenRouter…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>No models match your filter.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
            <thead>
              <tr style={{ background: '#f5f3ff' }}>
                {['Provider','Model','Context','Prompt / 1M tokens','Completion / 1M tokens','Est. DGCC / 1K tokens'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 9, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(124,58,237,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 12px' }}>
                    <ProviderPill provider={m.provider} accent={PROVIDER_COLORS[m.provider] || '#94a3b8'} />
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 12 }}>{m.name}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{m.id}</div>
                  </td>
                  <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 11 }}>
                    {m.context_length ? `${(m.context_length/1000).toFixed(0)}K` : '-'}
                  </td>
                  <td style={{ padding: '9px 12px', fontWeight: 700, color: '#7c3aed' }}>
                    {freeLabel(m.prompt_per_1m_usd)}
                  </td>
                  <td style={{ padding: '9px 12px', fontWeight: 700, color: '#5b21b6' }}>
                    {freeLabel(m.completion_per_1m_usd)}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {m.est_dgcc_per_1k > 0
                      ? <span style={{ background: 'rgba(124,58,237,0.08)', color: '#7c3aed', padding: '2px 8px', borderRadius: 5, fontWeight: 800, fontSize: 10 }}>{m.est_dgcc_per_1k.toFixed(4)} DGCC</span>
                      : <span style={{ color: '#10b981', fontWeight: 700, fontSize: 10 }}>Free tier</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10, color: '#94a3b8' }}>{models.length} models across {new Set(models.map(m => m.provider)).size} providers · Prices in USD per 1M tokens · Est. DGCC assumes 60/40 prompt/completion split</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ROOT EXPORT - AdminPricingView
// ──────────────────────────────────────────────────────────────────────────────
export function AdminPricingView() {
  const [toast, setToast] = useState(null);

  const onToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <KeiPricingPanel     onToast={onToast} />
      <OpenRouterPricingPanel onToast={onToast} />
      <Toast toast={toast} />
    </div>
  );
}
